import React, { useMemo } from 'react'
import { Tournament } from '../types'

const toNumber = (v: any) => {
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

const fmtInt = (v: any) => {
  const n = Math.round(toNumber(v))
  try { return n.toLocaleString('ru-RU') } catch { return String(n) }
}

const fmtMoney = (v: any) => {
  const n = toNumber(v)
  try { return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) } catch { return n.toFixed(2).replace('.', ',') }
}

interface Props {
  tournament: Tournament
  onClose: () => void
}

const TournamentBudgetBreakdownModal: React.FC<Props> = ({ tournament, onClose }) => {
  const saved = useMemo(() => {
    try {
      const raw = localStorage.getItem(`tournament-budget-${(tournament as any)?.id}`)
      return raw ? JSON.parse(raw) : null
    } catch { return null }
  }, [tournament])

  const levels: string[] = useMemo(() => {
    const list: any[] = Array.isArray((tournament as any).memberships) ? (tournament as any).memberships : []
    return Array.from(new Set(list.map(m => m.level).filter(Boolean)))
  }, [tournament])

  const byLevel = saved?.byLevel || {}
  const ballsAvg = toNumber(saved?.snapshot?.ballsAvg)
  const waterAvg = toNumber(saved?.snapshot?.waterAvg)
  const courtRate = toNumber(saved?.courtRate)
  const cups = toNumber(saved?.tournamentCups)
  const otherTournament = toNumber(saved?.tournamentOther)
  const otherExpensesTotal = (Array.isArray(saved?.expenses) ? saved.expenses : []).reduce((s: number, e: any) => s + toNumber(e.amount), 0)

  const feesByLevel: Record<string, number> = useMemo(() => {
    const map: Record<string, number> = {}
    const list: any[] = Array.isArray((tournament as any).memberships) ? (tournament as any).memberships : []
    list.forEach(m => {
      const l = m.level
      if (!l) return
      map[l] = (map[l] || 0) + toNumber(m.fee)
    })
    return map
  }, [tournament])
  const feesCountByLevel: Record<string, number> = useMemo(() => {
    const map: Record<string, number> = {}
    const list: any[] = Array.isArray((tournament as any).memberships) ? (tournament as any).memberships : []
    list.forEach(m => {
      const l = m.level
      if (!l) return
      map[l] = (map[l] || 0) + 1
    })
    return map
  }, [tournament])

  const perLevel = useMemo(() => {
    const map: Record<string, any> = {}
    levels.forEach(l => {
      const p = byLevel[l] || {}
      const games = toNumber(p.games)
      const hours = games * toNumber(p.gameHours)
      const ballsUsed = games * toNumber(p.ballsPerGame)
      const ballsCost = ballsUsed * ballsAvg
      const lost = ballsUsed * (toNumber(p.lostPercent) / 100)
      const sellable = Math.max(ballsUsed - lost, 0)
      const firstQty = sellable * (toNumber(p.firstGradePercent) / 100)
      const secondQty = sellable - firstQty
      const revenueBalls = firstQty * toNumber(p.priceFirstGrade) + secondQty * toNumber(p.priceSecondGrade)
      const waterQty = games * toNumber(p.waterPerGame)
      const waterCost = waterQty * waterAvg
      const other = toNumber(p.otherExpense)
      const cups = toNumber((p as any).cupsExpense ?? (p as any).cups)
      map[l] = { games, hours, ballsUsed, ballsCost, lost, firstQty, secondQty, waterQty, waterCost, other, cups, revenueBalls, p }
    })
    return map
  }, [levels, byLevel, ballsAvg, waterAvg])

  const totalHours = Object.values(perLevel).reduce((s: number, v: any) => s + toNumber((v as any).hours), 0)
  const courtCostTotal = totalHours * courtRate

  const courtByLevel: Record<string, number> = {}
  levels.forEach(l => {
    const h = toNumber(perLevel[l]?.hours)
    courtByLevel[l] = totalHours > 0 ? (h / totalHours) * courtCostTotal : 0
  })

  const cols = ['Итого', ...levels]

  const row = (label: string, getter: (level?: string) => number, bold = false, explain?: (level?: string) => string) => {
    const values = levels.map(l => getter(l))
    const total = values.reduce((s, v) => s + v, 0) + (getter(undefined) || 0)
    const explValues = levels.map(l => (explain ? explain(l) : ''))
    const explTotal = explain ? explain(undefined) : ''
    return { label, total, byLevel: values, bold, explByLevel: explValues, explTotal }
  }

  // Доходы
  const rFees = row('Взносы игроков', (l?: string) => l ? toNumber(feesByLevel[l]) : 0, false, (l?: string) => {
    if (!l) {
      const totalCount = levels.reduce((s, lv) => s + toNumber(feesCountByLevel[lv]), 0)
      const totalAmount = levels.reduce((s, lv) => s + toNumber(feesByLevel[lv]), 0)
      const avg = totalCount > 0 ? totalAmount / totalCount : 0
      return totalCount ? `${fmtInt(totalCount)}×${fmtMoney(avg)}` : ''
    }
    const cnt = toNumber(feesCountByLevel[l])
    const amount = toNumber(feesByLevel[l])
    const avg = cnt > 0 ? amount / cnt : 0
    return cnt ? `${fmtInt(cnt)}×${fmtMoney(avg)}` : ''
  })
  const rBallRevenue = row('Выручка от мячей', (l?: string) => l ? toNumber(perLevel[l]?.revenueBalls) : 0, false, (l?: string) => {
    if (!l) return ''
    const P = (perLevel[l] as any) || {}
    const p = (P.p || {}) as any
    const q1 = toNumber(P.firstQty)
    const q2 = toNumber(P.secondQty)
    const pr1 = toNumber(p.priceFirstGrade)
    const pr2 = toNumber(p.priceSecondGrade)
    return `${fmtInt(q1)}×${fmtMoney(pr1)} + ${fmtInt(q2)}×${fmtMoney(pr2)}`
  })
  // Формула под «Итого» как сумма формул по уровням
  rBallRevenue.explTotal = rBallRevenue.explByLevel
    .filter((s: string) => !!s)
    .join(' + ')
  const rIncomeTotal = row('Итого доходы', (l?: string) => (toNumber(feesByLevel[l||'']) + toNumber(perLevel[l||'']?.revenueBalls) || 0), true)

  // Расходы
  const rCourt = row('Аренда кортов', (l?: string) => l ? toNumber(courtByLevel[l]) : 0, false, (l?: string) => {
    if (!l) return `${fmtInt(totalHours)}×${fmtMoney(courtRate)}`
    return `${fmtInt(perLevel[l]?.hours)}×${fmtMoney(courtRate)}`
  })
  const rBallsCost = row('Себест. мячи', (l?: string) => l ? toNumber(perLevel[l]?.ballsCost) : 0, false, (l?: string) => {
    if (!l) {
      const totalBalls = levels.reduce((s, lv) => s + toNumber(perLevel[lv]?.ballsUsed), 0)
      return `${fmtInt(totalBalls)}×${fmtMoney(ballsAvg)}`
    }
    return `${fmtInt(perLevel[l]?.ballsUsed)}×${fmtMoney(ballsAvg)}`
  })
  const rWaterCost = row('Себест. вода', (l?: string) => l ? toNumber(perLevel[l]?.waterCost) : 0, false, (l?: string) => {
    if (!l) {
      const totalWater = levels.reduce((s, lv) => s + toNumber(perLevel[lv]?.waterQty), 0)
      return `${fmtInt(totalWater)}×${fmtMoney(waterAvg)}`
    }
    return `${fmtInt(perLevel[l]?.waterQty)}×${fmtMoney(waterAvg)}`
  })
  const rOtherLvl = row('Прочее (уровни)', (l?: string) => l ? toNumber(perLevel[l]?.other) : 0)
  const rCupsLvl = row('Призы/Кубки (уровни)', (l?: string) => l ? toNumber(perLevel[l]?.cups) : 0)
  const rOtherTournament = row('Прочее (турнир)', (_?: string) => 0, false)
  rOtherTournament.total = otherTournament
  const rOther = row('Прочие расходы', (_?: string) => 0, false)
  rOther.total = otherExpensesTotal
  const rExpenseTotal = row('Итого расходы', (l?: string) => (
    toNumber(courtByLevel[l||'']) + toNumber(perLevel[l||'']?.ballsCost) + toNumber(perLevel[l||'']?.waterCost) + toNumber(perLevel[l||'']?.other) + toNumber(perLevel[l||'']?.cups)
  ), true)
  rExpenseTotal.total += otherTournament + otherExpensesTotal

  const profitByLevel = levels.map((l, idx) => rIncomeTotal.byLevel[idx] - rExpenseTotal.byLevel[idx])
  const profitTotal = rIncomeTotal.total - rExpenseTotal.total

  const rows = [
    { section: 'Доходы', data: [rFees, rBallRevenue, rIncomeTotal] },
    { section: 'Расходы', data: [rCourt, rBallsCost, rWaterCost, rOtherLvl, rCupsLvl, rOtherTournament, rOther, rExpenseTotal] },
    { section: 'Прибыль', data: [{ label: 'Итого прибыль', total: profitTotal, byLevel: profitByLevel, bold: true }] }
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black opacity-30" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-lg border w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Бюджет (расшифровка): {tournament.name}</h3>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>✕</button>
        </div>
        <div className="p-4 space-y-4">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1">Статья</th>
                  {cols.map(c => (
                    <th key={c} className="text-right px-2 py-1 whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(block => (
                  <React.Fragment key={block.section}>
                    <tr>
                      <td className="px-2 py-2 text-gray-700 font-medium" colSpan={cols.length + 1}>{block.section}</td>
                    </tr>
                    {block.data.map((r: any) => (
                      <tr key={r.label} className={r.bold ? 'font-semibold' : ''}>
                        <td className="px-2 py-1">{r.label}</td>
                        <td className="px-2 py-1 text-right">
                          <div>{fmtMoney(r.total)}</div>
                          {r.explTotal ? <div className="text-xs text-gray-500">{r.explTotal}</div> : null}
                        </td>
                        {levels.map((l, idx) => (
                          <td key={l} className="px-2 py-1 text-right">
                            <div>{fmtMoney(r.byLevel?.[idx])}</div>
                            {r.explByLevel?.[idx] ? <div className="text-xs text-gray-500">{r.explByLevel[idx]}</div> : null}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-4 py-3 border-t flex justify-end">
          <button className="px-3 py-2 text-sm rounded-md border text-gray-700 bg-white hover:bg-gray-50" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  )
}

export default TournamentBudgetBreakdownModal


